import { Card, Grid, Group, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { DepartmentDto } from '@mexp/shared';
import { http } from '@/api/client';
import { useCurrentUser } from '@/auth/AuthProvider';
import { RoleBadge } from '@/components/badges';
import { PageHeader } from '@/components/PageHeader';
import { notifySuccess } from '@/lib/notify';
import { ChangePasswordForm } from './ChangePasswordForm';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack gap={2}>
      <Text fz="xs" c="dimmed" tt="uppercase" fw={600} lts={0.3}>
        {label}
      </Text>
      <Text fz="sm">{value}</Text>
    </Stack>
  );
}

export function ProfilePage() {
  const user = useCurrentUser();
  const { hash } = useLocation();
  const passwordRef = useRef<HTMLDivElement>(null);
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => http.get<{ items: DepartmentDto[] }>('/departments'),
    enabled: user.userType === 'staff',
  });
  const departmentName = departments.data?.items.find((d) => d.code === user.department)?.name ?? user.department ?? '—';

  useEffect(() => {
    if (hash === '#password') passwordRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [hash]);

  return (
    <>
      <PageHeader title="My account" description="Your profile and sign-in details." />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card>
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>{user.fullName}</Title>
                <RoleBadge role={user.role} />
              </Group>
              <Field label="Email" value={user.email} />
              {user.userType === 'staff' && <Field label="Department" value={departmentName} />}
              <Text fz="xs" c="dimmed">
                To change your name, role or department, ask an administrator.
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card ref={passwordRef} id="password">
            <Stack gap="md">
              <Title order={4}>Change password</Title>
              <ChangePasswordForm onDone={() => notifySuccess('Your password has been updated. Other devices were signed out.')} />
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>
    </>
  );
}
